import apiClient from '@/lib/api/apiClient'
import { extractErrorMessage } from '@/lib/utils/errorHandling'
import type {
  SystemAnnouncement,
  SystemAnnouncementListParams,
  SystemAnnouncementListResponse,
  SystemAnnouncementSubmitPayload,
  OrgOption,
} from '../types'

const BASE = '/api/system_announcements'

export async function getSystemAnnouncements(
  params: SystemAnnouncementListParams = {},
): Promise<SystemAnnouncementListResponse> {
  try {
    const response = await apiClient.get<SystemAnnouncementListResponse>(BASE, { params })
    return response.data
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load system announcements')
  }
}

export async function getSystemAnnouncement(saId: number): Promise<SystemAnnouncement> {
  try {
    const response = await apiClient.get<SystemAnnouncement>(`${BASE}/${saId}`)
    return response.data
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load system announcement')
  }
}

export async function saveSystemAnnouncement(
  payload: SystemAnnouncementSubmitPayload,
  saId?: number,
): Promise<SystemAnnouncement> {
  try {
    const url = saId ? `${BASE}/${saId}` : BASE
    const response = await apiClient.post<SystemAnnouncement>(url, payload)
    return response.data
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to save system announcement')
  }
}

export async function deleteSystemAnnouncement(saId: number): Promise<void> {
  try {
    await apiClient.delete(`${BASE}/${saId}`)
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to delete system announcement')
  }
}

export async function searchAllOrganizations(query?: string): Promise<OrgOption[]> {
  try {
    const params = query ? { q: query } : {}
    const response = await apiClient.get<Array<{ org_id: number; org_name: string }>>(
      '/api/all_organizations',
      { params },
    )
    return (response.data ?? []).map((o) => ({ label: o.org_name, value: o.org_id }))
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load organizations')
  }
}

/**
 * POST /api/notification/{sa_id} — mark a system announcement as
 * acknowledged. Mirrors Angular `popupSubmit('2')` in the
 * SystemAnnouncementDetailComponent (the resident-facing acknowledgement
 * popup, separate from the admin tab but on the same module). Available
 * here so any consumer of an announcement detail modal can mark-as-read.
 */
export async function markAnnouncementRead(saId: number): Promise<void> {
  try {
    await apiClient.post(`/api/notification/${saId}`)
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to mark announcement as read')
  }
}

/**
 * GET /api/system_announcement — org welcome/banner HTML shown on the staff
 * dashboard (Angular `loadSystemAnnouncement`). Separate from the acknowledgement
 * popup, which uses role `notification` data from change_role.
 */
export async function getDashboardSystemAnnouncementBanner(): Promise<string> {
  try {
    const response = await apiClient.get<string | { data?: string }>('/api/system_announcement')
    const data = response.data
    if (typeof data === 'string') return data
    if (data && typeof data === 'object' && typeof data.data === 'string') return data.data
    return ''
  } catch {
    return ''
  }
}
